import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Crosshair, Trophy, ShieldCheck, Users, Flame, Gamepad2, Heart } from "lucide-react";

const STATS = [
  { icon: Trophy, label: "Max Rep", value: "Multiple 2Ks" },
  { icon: Gamepad2, label: "Playing Since", value: "2K9 · Park in 2K14" },
  { icon: Flame, label: "Hours Played", value: "Thousands+" },
];

const VALUES = [
  { icon: ShieldCheck, title: "Real people, real vetting", text: "Every report is reviewed by actual modders who know the game — not a bot, not a guess. If it hits the wall, it earned it." },
  { icon: Users, title: "Powered by the community", text: "Players who faced the same cheater confirm it. Strength in numbers is what makes a case undeniable." },
  { icon: Crosshair, title: "One mission: clean lobbies", text: "Cronus, auto-green macros, lag switches, account sharing, stat boosting, glitch abuse — we surface it and push it to the 2K dev team for permanent bans." },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#1E293F]">
        <div className="absolute inset-0 grid-texture opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF2A55]/5 via-transparent to-[#090C15]" />
        <div className="relative mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-[#FF2A55]/30 bg-[#FF2A55]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#FF2A55]">
            <Heart size={13} /> Built out of love for 2K
          </div>
          <h1 className="animate-fade-up mt-5 font-heading text-4xl font-black leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            Why <span className="text-[#00F0FF]">2K Case Files</span> exists
          </h1>
          <p className="animate-fade-up mt-4 max-w-2xl text-lg text-[#94A3B8]" style={{ animationDelay: "160ms" }}>
            A community-run anti-cheat wall for NBA 2K — created by a lifelong hooper who's tired of watching cheaters ruin the game we love.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        {/* Stats strip */}
        <div className="animate-fade-up grid grid-cols-1 gap-3 sm:grid-cols-3">
          {STATS.map((s) => (
            <div key={s.label} className="flex items-center gap-3 rounded-xl border border-[#1E293F] bg-[#0F1424] p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#00F0FF]/15 text-[#00F0FF]"><s.icon size={18} /></span>
              <div>
                <div className="font-heading text-base font-bold text-white">{s.value}</div>
                <div className="text-xs uppercase tracking-wider text-[#64748B]">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Founder story */}
        <div className="animate-fade-up mt-10 rounded-2xl border border-[#1E293F] bg-[#0F1424] p-6 sm:p-8" data-testid="founder-story">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">From the founder</h2>
          <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-[#CBD5E1]">
            <p>
              I built this out of love for 2K. I've been playing since <span className="font-semibold text-white">2K9</span>, and I was there when <span className="font-semibold text-white">Park first dropped in 2K14</span> — the mode that changed everything. From that moment I was hooked, and I dove deeper than most.
            </p>
            <p>
              Over the years I got so locked in that I came to understand pretty much everything the game has to offer — how to shoot, what builds and animations to run, the little things that separate good from great. A lot of it I figured out on my own, <span className="font-semibold text-white">self-taught long before any of it was common knowledge</span> or in a YouTube guide.
            </p>
            <p>
              I've put in <span className="font-semibold text-white">thousands upon thousands of hours</span> across every 2K, and I've hit <span className="text-[#00FF87] font-semibold">max rep on multiple titles</span>. That grind meant something. But lately everything I earned and everything I learned is being cheapened by <span className="text-[#FF2A55] font-semibold">cheaters</span> — people using Cronus, macros, lag switches and glitches to skip the work and ruin the experience for everyone else.
            </p>
            <p>
              That's the whole reason 2K Case Files exists. Not to gatekeep, not to hate — but to protect the game I love and the players who actually put in the work. My goal is simple: <span className="font-semibold text-white">gather undeniable proof, have real modders vet it, and get confirmed cheaters permanently banned</span> so they can't keep ruining lobbies.
            </p>
            <p className="text-[#94A3B8]">
              If you love 2K the way I do, this wall is for you. Report what you see, back up the reports you've faced, and let's clean up the game together. 🏀
            </p>
          </div>
        </div>

        {/* Values */}
        <h2 className="animate-fade-up mt-12 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">What we stand for</h2>
        <div className="animate-fade-up mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          {VALUES.map((v) => (
            <div key={v.title} className="rounded-xl border border-[#1E293F] bg-[#0F1424] p-5">
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#FF2A55]/15 text-[#FF2A55]"><v.icon size={20} /></span>
              <h3 className="mt-3 font-heading text-lg font-bold text-white">{v.title}</h3>
              <p className="mt-1.5 text-sm text-[#94A3B8]">{v.text}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="animate-fade-up mt-12 overflow-hidden rounded-2xl border border-[#00F0FF]/30 bg-gradient-to-r from-[#141B2D] to-[#0F1424] p-8 text-center">
          <Crosshair className="mx-auto text-[#00F0FF]" size={30} />
          <h3 className="mt-3 font-heading text-2xl font-black tracking-tight text-white">Spotted a cheater? Put them on the wall.</h3>
          <p className="mx-auto mt-2 max-w-md text-[#94A3B8]">It takes 60 seconds and helps get them banned for good.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button data-testid="about-report-btn" onClick={() => navigate("/submit")} className="bg-[#FF2A55] font-bold text-white hover:bg-[#FF2A55]/90">
              <Crosshair size={16} className="mr-2" /> Report a Cheater
            </Button>
            <Button data-testid="about-wall-btn" onClick={() => navigate("/")} variant="outline" className="border-[#1E293F] bg-transparent text-white hover:bg-[#141B2D] hover:text-white">
              Browse the Wall
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
